import React, { useState, useRef } from 'react';
import { 
  Users, 
  ShieldCheck, 
  UserPlus, 
  Mail, 
  MapPin, 
  Shield, 
  Key, 
  Trash2, 
  Search,
  CheckCircle2,
  AlertCircle,
  ToggleRight,
  ToggleLeft,
  Settings,
  LayoutDashboard,
  ClipboardList,
  CalendarDays,
  ShieldAlert,
  FileText,
  X,
  Lock,
  User,
  Plus,
  Camera,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { AppUser, UserRole, AppFeature, RolePermissions } from '../types';

interface UserManagementProps {
  users: AppUser[];
  updateUser: (user: AppUser) => void;
  deleteUser: (id: string) => void;
  addUser: (user: AppUser) => void;
  permissions: RolePermissions;
  updatePermissions: (role: UserRole, features: AppFeature[]) => void;
}

const FEATURE_LABELS: Record<AppFeature, { label: string, icon: React.ReactNode }> = {
  [AppFeature.DASHBOARD]: { label: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
  [AppFeature.MASTER_DATA]: { label: 'Master Data', icon: <Settings className="w-4 h-4" /> },
  [AppFeature.SCHEDULE]: { label: 'Schedule', icon: <CalendarDays className="w-4 h-4" /> },
  [AppFeature.INPUT_ACTUAL]: { label: 'Audit Hub', icon: <ClipboardList className="w-4 h-4" /> },
  [AppFeature.MONITORING]: { label: 'Monitoring', icon: <ShieldAlert className="w-4 h-4" /> },
  [AppFeature.FINDINGS]: { label: 'Findings', icon: <AlertCircle className="w-4 h-4" /> },
  [AppFeature.REPORTS]: { label: 'Reports', icon: <FileText className="w-4 h-4" /> },
  [AppFeature.USERS]: { label: 'User Admin', icon: <Users className="w-4 h-4" /> },
};

const ROLE_CONFIG: Record<UserRole, { color: string, icon: React.ReactNode, desc: string }> = {
  [UserRole.SUPER_ADMIN]: { color: 'bg-emerald-500', icon: <ShieldCheck className="w-4 h-4" />, desc: 'Root access.' },
  [UserRole.ADMIN]: { color: 'bg-amber-500', icon: <Shield className="w-4 h-4" />, desc: 'Plant controller.' },
  [UserRole.MANAGEMENT]: { color: 'bg-blue-500', icon: <Key className="w-4 h-4" />, desc: 'Strategic review.' },
  [UserRole.INSPECTOR]: { color: 'bg-indigo-500', icon: <Search className="w-4 h-4" />, desc: 'Field auditor.' },
  [UserRole.PIC]: { color: 'bg-rose-500', icon: <CheckCircle2 className="w-4 h-4" />, desc: 'Task resolver.' },
};

/**
 * Utility to compress image to stay under Firestore 1MB limit.
 */
const compressImage = (base64Str: string, maxWidth = 300, quality = 0.5): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64Str;
    img.onerror = reject;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error("Canvas context error"));
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
  });
};

const UserManagement: React.FC<UserManagementProps> = ({ users, updateUser, deleteUser, addUser, permissions, updatePermissions }) => {
  const [activeView, setActiveView] = useState<'users' | 'access'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editPhotoInputRef = useRef<HTMLInputElement>(null);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: UserRole.INSPECTOR,
    plant: '',
    avatar: ''
  });

  const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()));

  const togglePermission = (role: UserRole, feature: AppFeature) => {
    if (role === UserRole.SUPER_ADMIN) return;
    const current = permissions[role] || [];
    const updated = current.includes(feature) 
      ? current.filter(f => f !== feature)
      : [...current, feature];
    updatePermissions(role, updated);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsCompressing(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result as string;
          const compressed = await compressImage(base64);
          setNewUser(prev => ({ ...prev, avatar: compressed }));
        } catch (err) {
          console.error("User avatar compression failed", err);
        } finally {
          setIsCompressing(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>, user: AppUser) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsCompressing(true);
      setUpdatingUserId(user.id);
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result as string;
          const compressed = await compressImage(base64);
          updateUser({ ...user, avatar: compressed });
        } catch (err) {
          console.error("User photo update failed", err);
        } finally {
          setIsCompressing(false);
          setUpdatingUserId(null);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const user: AppUser = {
      id: `u-${Date.now()}`,
      name: newUser.name,
      email: newUser.email,
      password: newUser.password || 'password123',
      role: newUser.role,
      plant: newUser.plant || '', // Always use string to prevent undefined in Firestore
      avatar: newUser.avatar || `https://i.pravatar.cc/150?u=${Date.now()}`
    };
    addUser(user);
    setShowAddModal(false);
    setNewUser({ name: '', email: '', password: '', role: UserRole.INSPECTOR, plant: '', avatar: '' });
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Control Center</h1>
          <p className="text-slate-500 mt-2 font-medium italic">Manage identities and module access authorization.</p>
        </div>
        <div className="flex bg-white p-1 rounded-2xl border border-slate-100 shadow-sm">
            <button onClick={() => setActiveView('users')} className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeView === 'users' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400'}`}>Identities</button>
            <button onClick={() => setActiveView('access')} className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeView === 'access' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400'}`}>Access Matrix</button>
        </div>
      </div>

      {activeView === 'users' ? (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
                <div className="flex-1 relative max-w-xl">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" placeholder="Find user..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-6 py-4 bg-white border border-slate-200 rounded-2xl text-xs font-bold outline-none shadow-sm focus:ring-4 focus:ring-emerald-500/10 transition-all" />
                </div>
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-slate-900/20 flex items-center gap-3 hover:bg-emerald-600 transition-all"
                >
                  <UserPlus className="w-5 h-5" /> Provision User
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredUsers.map((user) => (
                    <div key={user.id} className="p-8 bg-white border border-slate-100 rounded-[3rem] shadow-sm hover:shadow-2xl transition-all group relative">
                        <div className="flex items-start justify-between mb-8">
                            <div className="relative group/avatar">
                                <img src={user.avatar} className="w-16 h-16 rounded-[1.5rem] object-cover border-4 border-slate-50 shadow-md transition-all group-hover/avatar:brightness-75" alt={user.name} />
                                <button 
                                  onClick={() => editPhotoInputRef.current?.click()}
                                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity text-white"
                                >
                                  {isCompressing && updatingUserId === user.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                                </button>
                                <input type="file" ref={editPhotoInputRef} className="hidden" accept="image/*" onChange={(e) => handleEditPhotoUpload(e, user)} />
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                <button onClick={() => deleteUser(user.id)} className="p-2 text-slate-200 hover:text-rose-500"><Trash2 className="w-5 h-5" /></button>
                            </div>
                        </div>
                        <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{user.name}</h3>
                        <div className="flex flex-col gap-1 mt-2">
                          <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold">
                              <Mail className="w-3.5 h-3.5" /> {user.email}
                          </div>
                          {user.password && (
                            <div className="flex items-center gap-2 text-slate-300 text-[9px] font-medium italic">
                                <Lock className="w-3 h-3" /> Encrypted Credentials
                            </div>
                          )}
                        </div>
                        <div className="space-y-4 pt-6 mt-6 border-t border-slate-100">
                            <div>
                                <label className="text-[9px] font-black uppercase text-slate-300 tracking-widest block mb-2">Role Authority</label>
                                <div className="relative">
                                    <select 
                                      value={user.role} 
                                      onChange={(e) => updateUser({...user, role: e.target.value as UserRole})} 
                                      className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 text-[10px] font-black uppercase outline-none focus:ring-2 focus:ring-emerald-500/20 appearance-none"
                                    >
                                        {Object.values(UserRole).map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    <Shield className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300 pointer-events-none" />
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-500">
                                  <MapPin className="w-3.5 h-3.5 text-slate-400" /> {user.plant || 'Global Access'}
                              </div>
                              <div className={`w-2 h-2 rounded-full ${ROLE_CONFIG[user.role].color} animate-pulse`}></div>
                            </div>
                        </div>
                    </div>
                ))}
                
                {filteredUsers.length === 0 && (
                    <div className="col-span-full py-20 text-center space-y-4 bg-white/50 rounded-[4rem] border-2 border-dashed border-slate-200">
                        <Users className="w-12 h-12 text-slate-200 mx-auto" />
                        <p className="text-slate-400 font-bold italic">No matching identities found.</p>
                    </div>
                )}
            </div>
        </div>
      ) : (
        <div className="bg-white rounded-[4rem] border border-slate-100 shadow-sm overflow-hidden animate-in slide-in-from-bottom-12 duration-500">
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="px-10 py-8 text-[11px] font-black text-slate-400 uppercase tracking-widest">Organizational Role</th>
                            {Object.values(AppFeature).map(f => (
                                <th key={f} className="px-6 py-8 text-center min-w-[120px]">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="text-slate-400">{FEATURE_LABELS[f].icon}</div>
                                        <span className="text-[9px] font-black uppercase text-slate-500 tracking-tighter">{FEATURE_LABELS[f].label}</span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {Object.values(UserRole).map(role => (
                            <tr key={role} className="hover:bg-slate-50/50 group">
                                <td className="px-10 py-8">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 ${ROLE_CONFIG[role].color} text-white rounded-xl flex items-center justify-center shadow-lg`}>
                                            {ROLE_CONFIG[role].icon}
                                        </div>
                                        <div>
                                            <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{role}</p>
                                            <p className="text-[10px] text-slate-400 font-medium italic">{ROLE_CONFIG[role].desc}</p>
                                        </div>
                                    </div>
                                </td>
                                {Object.values(AppFeature).map(feature => {
                                    const isEnabled = (permissions[role] || []).includes(feature);
                                    return (
                                        <td key={feature} className="px-6 py-8 text-center">
                                            <button 
                                                disabled={role === UserRole.SUPER_ADMIN}
                                                onClick={() => togglePermission(role, feature)}
                                                className={`transition-all ${role === UserRole.SUPER_ADMIN ? 'cursor-not-allowed opacity-100' : 'hover:scale-110 active:scale-95'}`}
                                            >
                                                {isEnabled ? (
                                                    <ToggleRight className="w-10 h-10 text-emerald-500" />
                                                ) : (
                                                    <ToggleLeft className="w-10 h-10 text-slate-200" />
                                                )}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="p-10 bg-slate-950 text-white flex items-center gap-6">
                <ShieldAlert className="w-10 h-10 text-emerald-400" />
                <div>
                    <h4 className="text-sm font-black uppercase tracking-widest mb-1">Authorization Guard</h4>
                    <p className="text-xs text-slate-500 font-medium leading-relaxed italic">Changes to the Access Matrix are applied instantly. Users must refresh or navigate to reflect new permission sets. Super Admin has permanent root access to prevent lockout.</p>
                </div>
            </div>
        </div>
      )}

      {/* Provision User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="bg-white rounded-[4rem] w-full max-w-2xl shadow-2xl overflow-hidden scale-in">
            <form onSubmit={handleAddSubmit}>
              <div className="p-12 border-b border-slate-100 flex justify-between items-start bg-slate-50">
                <div>
                  <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Provision New User</h3>
                  <p className="text-slate-500 font-medium mt-2 italic">Grant access to the safety infrastructure.</p>
                </div>
                <button type="button" onClick={() => setShowAddModal(false)} className="p-4 text-slate-400 hover:text-slate-900 bg-white rounded-2xl shadow-sm"><X className="w-8 h-8" /></button>
              </div>

              <div className="p-12 grid grid-cols-1 md:grid-cols-2 gap-12 max-h-[60vh] overflow-y-auto custom-scrollbar">
                <div className="space-y-8">
                  <div className="space-y-2 text-center">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] block mb-2">Profile Photo</label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square w-full max-w-[240px] mx-auto bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2.5rem] flex flex-col items-center justify-center cursor-pointer overflow-hidden relative group hover:border-emerald-500 transition-all shadow-inner"
                    >
                      {newUser.avatar ? (
                        <img src={newUser.avatar} className="w-full h-full object-cover" alt="Preview" />
                      ) : (
                        <div className="text-center">
                          {isCompressing ? (
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                          ) : (
                            <>
                              <Camera className="w-10 h-10 text-slate-300 group-hover:text-emerald-500 transition-all" />
                              <p className="text-[8px] font-black mt-4 text-slate-400 tracking-[0.3em] uppercase">Upload Avatar</p>
                            </>
                          )}
                        </div>
                      )}
                      <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Assigned Authority</label>
                    <select 
                      value={newUser.role}
                      onChange={e => setNewUser({...newUser, role: e.target.value as UserRole})}
                      className="w-full px-6 py-5 bg-slate-50 border border-slate-200 rounded-3xl text-xs font-black uppercase tracking-widest outline-none appearance-none cursor-pointer"
                    >
                      {Object.values(UserRole).map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Full Identity Name</label>
                    <div className="relative">
                      <User className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                      <input 
                        required
                        type="text" 
                        placeholder="Alexander Safety"
                        value={newUser.name}
                        onChange={e => setNewUser({...newUser, name: e.target.value})}
                        className="w-full pl-14 pr-6 py-5 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Corporate Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                      <input 
                        required
                        type="email" 
                        placeholder="user@corporate.com"
                        value={newUser.email}
                        onChange={e => setNewUser({...newUser, email: e.target.value})}
                        className="w-full pl-14 pr-6 py-5 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">System Password</label>
                    <div className="relative">
                      <Lock className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                      <input 
                        required
                        type="password" 
                        placeholder="••••••••"
                        value={newUser.password}
                        onChange={e => setNewUser({...newUser, password: e.target.value})}
                        className="w-full pl-14 pr-6 py-5 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] ml-1">Operational Site</label>
                    <div className="relative">
                      <MapPin className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                      <input 
                        type="text" 
                        placeholder="E.g. Plant 1"
                        value={newUser.plant}
                        onChange={e => setNewUser({...newUser, plant: e.target.value})}
                        className="w-full pl-14 pr-6 py-5 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-12 bg-slate-50 border-t border-slate-100 flex justify-end gap-4">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-10 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black text-xs uppercase tracking-widest">Cancel</button>
                <button type="submit" disabled={isCompressing} className="px-12 py-5 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-[0.3em] shadow-2xl shadow-slate-900/40 hover:bg-emerald-600 transition-all flex items-center gap-3 disabled:opacity-50">
                  <Plus className="w-5 h-5" /> Provision Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;